import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import ProfileButton from "./ProfileButton.js";
import { apiGet } from "../../services/api";

function HeaderMobile() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeNestedSubmenu, setActiveNestedSubmenu] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const headerTexts = {
    ru: "ресми интернет-ресурсы",
    kz: "ресми интернет-ресурстар",
    en: "official internet resources",
  };

  const logos = {
    kz: "/assets/images/LogoKZ.png",
    ru: "/assets/images/LogoRU.png",
    en: "/assets/images/LogoEN.png",
  };

  const { currentLanguage, languages, changeLanguage, isActiveLanguage } =
    useLanguage();

  const currentLogo = logos[currentLanguage] || logos.kz;

  useEffect(() => {
    fetchMenuData();
  }, [currentLanguage]);

  // Убираем логику скрытия хедера при скролле - теперь хедер статичный
  // Закрываем меню при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".header-mobile__burger")
      ) {
        setIsMenuOpen(false);
        setActiveSubmenu(null);
        setActiveNestedSubmenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Блокируем скролл страницы когда меню открыто
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/menu/structure`
      );

      if (response.data && response.data.menuStructure) {
        setMenuData(response.data.menuStructure);
      } else {
        throw new Error("Invalid data structure received");
      }
    } catch (err) {
      console.error("Error fetching menu data:", err);
      setError("Ошибка при загрузке структуры меню");
    } finally {
      setLoading(false);
    }
  };

  const getRoutePath = (route) => {
    return route || "#";
  };

  const handleLinkClick = (e, route) => {
    if (
      window.location.pathname.startsWith("/pages/") &&
      route.startsWith("/pages/")
    ) {
      e.preventDefault();
      navigate(route);
      window.scrollTo(0, 0);
    }
    setIsMenuOpen(false);
    setActiveSubmenu(null);
    setActiveNestedSubmenu(null);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      setActiveSubmenu(null);
      setActiveNestedSubmenu(null);
    }
  };

  const toggleSubmenu = (parentId) => {
    if (activeSubmenu === parentId) {
      setActiveSubmenu(null);
      setActiveNestedSubmenu(null);
    } else {
      setActiveSubmenu(parentId);
      setActiveNestedSubmenu(null);
    }
  };

  const toggleNestedSubmenu = (itemId) => {
    if (activeNestedSubmenu === itemId) {
      setActiveNestedSubmenu(null);
    } else {
      setActiveNestedSubmenu(itemId);
    }
  };

  // Обработчик клика на весь header элемент меню
  const handleMenuItemHeaderClick = (e, parent) => {
    // Если у элемента есть подменю, то переключаем его состояние
    if (parent.items && parent.items.length > 0) {
      e.preventDefault();
      toggleSubmenu(parent.id);
    } else {
      // Если подменю нет, то переходим по ссылке
      handleLinkClick(e, getRoutePath(parent.route));
    }
  };

  // Обработчик клика на header подменю
  const handleSubmenuHeaderClick = (e, item) => {
    if (item.type === "section") {
      e.preventDefault();
      toggleNestedSubmenu(item.id);
    } else {
      handleLinkClick(e, `/pages${item.route}`);
    }
  };

  const renderMobileMenuItems = () => {
    if (loading)
      return <div className="mobile-menu__loading">Загрузка меню...</div>;
    if (error) return <div className="mobile-menu__error">{error}</div>;

    return menuData.map((parent) => (
      <div key={parent.id} className="mobile-menu-item">
        <div
          className="mobile-menu-item__header"
          onClick={(e) => handleMenuItemHeaderClick(e, parent)}
          style={{ cursor: "pointer" }}
        >
          <span className="mobile-menu-item__link">
            {parent.titles[currentLanguage] ||
              parent.titles.kz ||
              parent.titles.ru ||
              parent.titles.en}
          </span>
          {parent.items && parent.items.length > 0 && (
            <span
              className={`mobile-menu-item__toggle ${
                activeSubmenu === parent.id ? "active" : ""
              }`}
            >
              <span className="mobile-menu-item__arrow">▼</span>
            </span>
          )}
        </div>

        {activeSubmenu === parent.id &&
          parent.items &&
          parent.items.length > 0 && (
            <div className="mobile-submenu">
              {parent.items.map((item) => (
                <div key={item.id} className="mobile-submenu-item">
                  {item.type === "section" ? (
                    <>
                      <div
                        className="mobile-submenu-item__header"
                        onClick={(e) => handleSubmenuHeaderClick(e, item)}
                        style={{ cursor: "pointer" }}
                      >
                        <span className="mobile-submenu-item__label">
                          {item.titles[currentLanguage] ||
                            item.titles.kz ||
                            item.titles.ru ||
                            item.titles.en}
                        </span>
                        <span
                          className={`mobile-submenu-item__toggle ${
                            activeNestedSubmenu === item.id ? "active" : ""
                          }`}
                        >
                          <span className="mobile-submenu-item__arrow">▼</span>
                        </span>
                      </div>
                      {activeNestedSubmenu === item.id && (
                        <div className="mobile-nested-submenu">
                          {item.sectionItems &&
                            item.sectionItems.map((subItem) => (
                              <Link
                                key={subItem.id}
                                to={getRoutePath(`/pages${subItem.route}`)}
                                className="mobile-nested-submenu__link"
                                onClick={(e) =>
                                  handleLinkClick(e, `/pages${subItem.route}`)
                                }
                              >
                                {subItem.titles[currentLanguage] ||
                                  subItem.titles.kz ||
                                  subItem.titles.ru ||
                                  subItem.titles.en}
                              </Link>
                            ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={getRoutePath(`/pages${item.route}`)}
                      className="mobile-submenu-item__link"
                      onClick={(e) => handleLinkClick(e, `/pages${item.route}`)}
                    >
                      {item.titles[currentLanguage] ||
                        item.titles.kz ||
                        item.titles.ru ||
                        item.titles.en}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    ));
  };

  return (
    <header className="header-mobile">
      {/* Верхняя часть с логотипами */}
      <div
        className="header-mobile__top"
        onClick={() => navigate("/")}
        style={{ cursor: "pointer" }}
      >
        <div className="header-mobile__logo-left">
          <img src="/assets/images/logolib.png" alt="Logo" />
        </div>

        <div
          className="header-mobile__center"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/assets/images/oiu-top-logo.png"
            alt=""
            className="header-mobile__top-logo"
          />
          <img
            src={currentLogo}
            alt={`Logo ${currentLanguage.toUpperCase()}`}
            className="header-mobile__main-logo"
          />
          <h6 className="header-mobile__subtitle">
            {headerTexts[currentLanguage] || headerTexts.kz}
          </h6>
        </div>
      </div>

      {/* Навигационная панель */}
      <div className="header-mobile__nav">
        {/* Языковые переключатели */}
        <div className="header-mobile__lang-switches">
          {Object.values(languages).map((lang) => (
            <button
              key={lang.code}
              className={`header-mobile__lang-switch ${
                isActiveLanguage(lang.code) ? "active" : ""
              }`}
              onClick={() => handleLanguageChange(lang.code)}
              title={lang.fullName}
            >
              {lang.name}
            </button>
          ))}
        </div>

        {/* Контейнер для кнопки профиля и бургер-меню */}
        <div className="header-mobile__controls">
          {/* Кнопка профиля */}
          <ProfileButton
            variant="icon"
            className="header-mobile__profile-btn"
          />

          {/* Бургер-меню */}
          <button
            className={`header-mobile__burger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Открыть меню"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Мобильное меню */}
      <div className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu__content">{renderMobileMenuItems()}</div>
      </div>

      {/* Оверлей */}
      {isMenuOpen && (
        <div
          className="mobile-menu__overlay"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </header>
  );
}

export default HeaderMobile;
